import type { TImportDataDictionary } from "components/ImportData/type";
import { beneficiaryTransform, termTransform, titleTransform } from "components/ImportData/utils";
import type { BankingFieldKey } from "types/countryFeatures";
import MessageBlock from "components/shared/MessageBlock";
import { Spinner } from "components/shared/Spinner";
import { BodyHighlight } from "helpers/TypographyComponents";
import { formatPayloadMegabytes } from "helpers/jsonPayloadLimit";
import { translationsWithFallback } from "helpers/utils";
import type { Dispatch, SetStateAction } from "react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import type { ICustomField } from "store/customFieldsStore";
import type { TImportError } from "store/dataImportSlice";
import { bulkUpdates, getSignedURL, setPendingImports, uploadToBucket } from "store/dataImportSlice";
import type { IFile, TFileMetadata } from "store/filesHistorySlice";
import { ProcessingJobStatus } from "store/jobsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { TFetchStatus } from "store/types";
import type { TImportTargets } from "types";

import type { TFileData } from "..";
import { getImportTypeTranslation } from "..";
import StepTitle from "../StepTitle";
import StepWrapper from "../StepWrapper";
import styles from "./style.module.scss";
import UpdateErrors from "./UpdateErrors";

interface IConfirmationStep {
    activeStep: number;
    setActiveStep: Dispatch<SetStateAction<number>>;
    /**
     * Data that will be sent.
     */
    loadedData: TFileData | undefined;
    setLoadedData: Dispatch<SetStateAction<TFileData | undefined>>;
    /**
     * Column names of their file.
     */
    fileTitles: string[];
    /**
     * Columns and their matches to our  keys.
     */
    selectedValues: Record<string, string>;
    filename: string;
    /**
     * Type of data being updated.
     */
    element: TImportDataDictionary | undefined;
    /**
     * Company settings needed for import and/or transform.
     */
    companySettings: Record<string, boolean | string>;
    floatKeys: string[];
    closeModal: () => void;
    currentCustomFields: ICustomField[] | undefined;
    bankingFields?: BankingFieldKey[];
}

export type TPendingImport = {
    filename: string;
    date: Date;
    importType: Exclude<TImportTargets, "compute-analytics" | "handle-interforum-files">;
};

/**
 * Last step within bulk update to confirm upload and fix mistakes if necessary.
 */
const ConfirmationStep = ({
    activeStep,
    setActiveStep,
    loadedData,
    fileTitles,
    selectedValues,
    filename,
    element,
    companySettings,
    floatKeys,
    closeModal,
    setLoadedData,
    currentCustomFields,
    bankingFields,
}: IConfirmationStep) => {
    const [t, i18n] = useTranslation();
    const dispatch = useAppDispatch();

    const { isUploadAsync, usesVAT, companyCountryCode, internationalBankingEnabled } = companySettings;

    const { channels } = useAppSelector((state) => state.channels);
    const { userData } = useAppSelector((state) => state.settings);
    const { jobs } = useAppSelector((state) => state.jobs);

    const [updateErrors, setUpdateErrors] = useState<TImportError[]>([]);
    const [updateErrorMessage, setUpdateErrorMessage] = useState("");
    const [updateStatus, setUpdateStatus] = useState<TFetchStatus>("idle");

    const getServerErrorMessage = (payload: Record<string, any> | undefined): string => {
        if (
            payload?.error === "ServerError.PayloadTooLarge" &&
            typeof payload.payloadBytes === "number" &&
            typeof payload.limitBytes === "number"
        ) {
            return t("ImportData.PayloadTooLarge", {
                payloadSize: formatPayloadMegabytes(payload.payloadBytes, i18n.language),
                limitSize: formatPayloadMegabytes(payload.limitBytes, i18n.language),
            });
        }
        if (!payload) return t("ImportData.FailedToImportData");
        if (payload.data) return payload.data;
        return translationsWithFallback(payload.message, "ServerError.InternalServerError");
    };

    const jobsLength = +(sessionStorage.getItem("jobs_length") ?? 0);
    const oldPendingImports: TPendingImport[] = JSON.parse(sessionStorage.getItem("pending_imports") ?? "[]");

    /**
     * Removes from loadedData their file columns and all keys that do not have a match.
     * This readies data for import.
     */
    const cleanedData = useMemo(() => {
        if (!loadedData) return [];
        const newData = loadedData;
        newData?.forEach((line) => {
            Object.keys(line).map((item) => {});
            Object.keys(line).forEach((item) => {
                const matchExists = Object.keys(selectedValues).some((key) => selectedValues[key] === item);
                if (line[item] !== "Ignore" && !matchExists) {
                    delete line[item];
                }
            });
        });

        // Removes underscore from all keys so they match our values again.
        return newData.map((item) =>
            Object.fromEntries(Object.entries(item).map(([key, value]) => [key.replace("_", ""), value])),
        );
    }, [loadedData]);

    const numberLines = useMemo(() => {
        return cleanedData ? cleanedData.length : 0;
    }, [cleanedData, loadedData]);

    const formatData = (): TFileData | undefined => {
        if (!cleanedData) return;
        switch (element) {
            case "beneficiars":
                return beneficiaryTransform({
                    data: cleanedData,
                    update: true,
                    customFields: currentCustomFields ?? [],
                    bankingFields,
                    internationalBankingEnabled: internationalBankingEnabled as boolean,
                });
            case "titles":
                return titleTransform({
                    data: cleanedData,
                    usesVat: usesVAT as boolean,
                    channels,
                    isUpdate: true,
                    customFields: currentCustomFields ?? [],
                    companyCountryCode: companyCountryCode as string,
                });
            case "terms":
                return termTransform(cleanedData, channels);
        }
    };

    const getImportTargetType = (): TImportTargets => {
        switch (element) {
            case "beneficiars":
                return "beneficiar";
            case "titles":
                return "title";
            case "terms":
                return "terms";
            default:
                return "beneficiar";
        }
    };

    /**
     * Handles submit either for first import or for fixing errors.
     */
    const handleSubmit = async () => {
        if (updateStatus === "fulfilled") {
            closeModal();
            return;
        }

        const formattedData = formatData();
        if (!formattedData || !element) return;
        setUpdateStatus("pending");
        setUpdateErrors([]);
        setUpdateErrorMessage("");

        let response;

        if (isUploadAsync) {
            const URLResponse = await dispatch(getSignedURL({ method: "update", importType: getImportTargetType() }));

            if (URLResponse.meta.requestStatus === "fulfilled") {
                const URL = URLResponse.payload.data;
                response = await dispatch(uploadToBucket({ JSONData: formattedData, filename, URL }));

                if (response.meta.requestStatus === "fulfilled") {
                    const fakeImportData: Partial<IFile> & { importType: string } = {
                        filename,
                        date: new Date(),
                        importType: getImportTargetType(),
                        target: getImportTargetType(),
                        jobStatus: ProcessingJobStatus.PROCESSING,
                        user: userData?.email ?? "",
                        processingJobId: -1,
                        metadata: {} as TFileMetadata,
                        numberOfLines: formattedData.length,
                    };

                    const newImports = [...oldPendingImports, fakeImportData];

                    // Save updated pendingImports in session storage and store:
                    sessionStorage.setItem("pending_imports", JSON.stringify(newImports));
                    dispatch(setPendingImports(newImports));

                    // Save new jobs length in storage:
                    sessionStorage.setItem("jobs_length", ((jobsLength ? jobsLength : jobs.length) + 1).toString());

                    // Create toast:
                    const importLoadingToast = toast.loading(
                        `${t("AsyncUploads.PendingImports", { count: newImports.length })}`,
                        {
                            closeButton: true,
                        },
                    );
                }
            }
        } else {
            response = await dispatch(bulkUpdates({ data: formattedData, filename, element }));
        }

        if (!response) {
            setUpdateStatus("rejected");
            setUpdateErrorMessage(t("ImportData.FailedToImportData"));
            return;
        }

        setUpdateStatus(response.meta.requestStatus);

        if (response.meta.requestStatus === "rejected") {
            const payload = response.payload as Record<string, any> | undefined;
            if (payload?.error?.length) {
                setUpdateErrors(payload.error);
                setUpdateErrorMessage("");
            } else {
                setUpdateErrors([]);
                setUpdateErrorMessage(getServerErrorMessage(payload));
            }
        }
    };

    const renderConfirmationComponent = () => {
        switch (updateStatus) {
            case "pending":
                return (
                    <div className={styles["confirmation-step-pending"]}>
                        <Spinner />
                        <span style={{ marginTop: "2rem" }}>{t("ImportData.Pending")}</span>
                        <span style={{ marginTop: "5px" }}>{t("ImportData.Pending2")}</span>
                    </div>
                );
            case "fulfilled":
                return (
                    <div className={styles["confirmation-step-succeeded"]}>
                        <span>
                            {isUploadAsync
                                ? t("BulkUpdates.ConfirmationStep.Success1Async", { count: numberLines })
                                : t("BulkUpdates.ConfirmationStep.Success1", { count: numberLines })}
                        </span>
                        <span>{t("BulkUpdates.ConfirmationStep.Success2")}</span>
                    </div>
                );
            case "rejected":
                if (updateErrors.length > 0) {
                    return (
                        <UpdateErrors
                            errors={updateErrors}
                            fileTitles={fileTitles}
                            selectedValues={selectedValues}
                            loadedData={cleanedData}
                            floatKeys={floatKeys}
                            setLoadedData={setLoadedData}
                        />
                    );
                }

                return (
                    <div className={styles["confirmation-step-idle"]}>
                        <MessageBlock type="error" text={updateErrorMessage} style={{ maxWidth: "550px" }} />
                    </div>
                );
            default:
                return (
                    <div className={styles["confirmation-step-idle"]}>
                        <BodyHighlight style={{ color: "black", fontSize: "16px", fontWeight: "500" }}>
                            {t("BulkUpdates.ConfirmationStep.Pending", {
                                count: numberLines,
                                element: getImportTypeTranslation(element ?? "beneficiars"),
                            })}
                        </BodyHighlight>
                    </div>
                );
        }
    };

    const nextBtnText = () => {
        switch (updateStatus) {
            case "rejected":
                return t("BulkUpdates.ConfirmationStep.Confirm");
            case "fulfilled":
                return t("BulkUpdates.ConfirmationStep.Close");
            default:
                return undefined;
        }
    };

    return (
        <StepWrapper
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            handleSubmit={handleSubmit}
            customNextBtnText={nextBtnText()}
            hidePrevious={["fulfilled", "pending"].includes(updateStatus)}
            conditionToDisable={updateStatus === "pending"}
        >
            <StepTitle
                title={t("BulkUpdates.ConfirmationStep.Title")}
                subtitle={t("BulkUpdates.ConfirmationStep.Subtitle", {
                    element: getImportTypeTranslation(element ?? "beneficiars"),
                })}
            />
            {renderConfirmationComponent()}
        </StepWrapper>
    );
};

export default ConfirmationStep;
